import { Array, Null, Record, Result, Static, String } from "runtypes";
import type { Spec } from "vega";
import { Field } from "vega-lite/types_unstable/channeldef.js";
import {
  GenericFacetSpec,
  LayerSpec,
  TopLevel,
  UnitSpec,
} from "vega-lite/types_unstable/spec/index.js";

import { FilledDynamicValueTable } from "../DynamicValue.js";

/** A Vega-Lite Spec object */
export type VegaLiteSpec = TopLevel<LayerSpec<Field>>;

declare const VegaSpecTrustBrand: unique symbol;

/**
 * Trust records where a spec came from; it is not runtime validation or
 * sanitization. A spec is trusted only when controlled application code built
 * its structure. Specs loaded from cell outputs, persistence, kernels, or
 * other external inputs are untrusted because they may have been manipulated.
 */
type BrandedVegaSpec<TSpec, TTrust extends "trusted" | "untrusted"> = TSpec & {
  readonly [VegaSpecTrustBrand]: TTrust;
};

/**
 * A Vega-Lite spec generated structurally by controlled application code and
 * therefore permitted to render inline.
 */
export type TrustedVegaLiteSpec = BrandedVegaSpec<VegaLiteSpec, "trusted">;
/**
 * A Vega-Lite spec whose contents may have been manipulated externally. It
 * must render through the sandboxed iframe path, never inline.
 */
export type UntrustedVegaLiteSpec = BrandedVegaSpec<VegaLiteSpec, "untrusted">;

export type VegaLiteLayerOrFacetSpec =
  | TopLevel<LayerSpec<Field>>
  | TopLevel<GenericFacetSpec<UnitSpec<Field>, LayerSpec<Field>, Field>>;

/** A Vega Spec object */
export type VegaSpec = Spec;
/**
 * A Vega spec generated structurally by controlled application code and
 * therefore permitted to render inline.
 */
export type TrustedVegaSpec = BrandedVegaSpec<VegaSpec, "trusted">;
/**
 * A Vega spec whose contents may have been manipulated externally. It must
 * render through the sandboxed iframe path, never inline.
 */
export type UntrustedVegaSpec = BrandedVegaSpec<VegaSpec, "untrusted">;

/**
 * Asserts that controlled application code created this Vega spec. This does
 * not inspect or sanitize the spec. Call it only at the concrete creation
 * boundary; parsed, persisted, or externally supplied specs must stay
 * untrusted.
 */
export function markVegaSpecTrusted(vegaSpec: VegaSpec): TrustedVegaSpec {
  return vegaSpec as TrustedVegaSpec;
}

/**
 * Marks a Vega spec from an external or persisted source as untrusted because
 * its contents may have been manipulated outside controlled application code.
 */
export function markVegaSpecUntrusted(vegaSpec: VegaSpec): UntrustedVegaSpec {
  return vegaSpec as UntrustedVegaSpec;
}

/**
 * Asserts that controlled application code created this Vega-Lite spec. This
 * does not inspect or sanitize the spec. Call it only at the concrete creation
 * boundary; parsed, persisted, or externally supplied specs must stay
 * untrusted.
 */
export function markVegaLiteSpecTrusted(
  vegaLiteSpec: VegaLiteSpec,
): TrustedVegaLiteSpec {
  return vegaLiteSpec as TrustedVegaLiteSpec;
}

/**
 * Marks a Vega-Lite spec from an external or persisted source as untrusted
 * because its contents may have been manipulated outside controlled
 * application code.
 */
export function markVegaLiteSpecUntrusted(
  vegaLiteSpec: VegaLiteSpec,
): UntrustedVegaLiteSpec {
  return vegaLiteSpec as UntrustedVegaLiteSpec;
}

export type VegaSpecWithTrust =
  | { readonly trust: "trusted"; readonly spec: TrustedVegaSpec }
  | { readonly trust: "untrusted"; readonly spec: UntrustedVegaSpec };

export type VegaLiteSpecWithTrust =
  | { readonly trust: "trusted"; readonly spec: TrustedVegaLiteSpec }
  | { readonly trust: "untrusted"; readonly spec: UntrustedVegaLiteSpec };

export type VegaDisplayOverrides = {
  fontFamily?: string;
  background?: string;
  domainColor?: string;
  gridColor?: string;
  labelColor?: string;
  tickColor?: string;
  titleColor?: string;
  strokeColor?: string;
  defaultRuleColor?: string;
  titleFontWeight?: number;
  labelPadding?: number;
  axisTitlePadding?: number;
  legendTitlePadding?: number;
};

export const VegaLayer = Record({
  selectedDataFrameVariableName: String.Or(Null),
  outputDataFrameVariableName: String.optional(),
});

export const VegaMetadata = Record({
  byLayer: Array(VegaLayer),
});

export type VegaMetadata = Static<typeof VegaMetadata>;

export type VegaLayer = Static<typeof VegaLayer>;

export type VegaDataFrame = {
  name: string | null;
  table: Result<FilledDynamicValueTable>;
  truncated: boolean;
};

/** Names of custom formatters we support for data */
export const CustomVegaFormatTypes = {
  NUMBER_FORMATTER: "NUMBER_FORMATTER",
  DATETIME_FORMATTER: "DATETIME_FORMATTER",
};

export type CustomVegaFormatTypes =
  (typeof CustomVegaFormatTypes)[keyof typeof CustomVegaFormatTypes];
